import React, { useState, useMemo } from 'react';
import {
  Button,
  Select,
  SelectItem,
  Card,
  CardBody,
  Textarea,
  Chip,
  Spinner,
  Input,
} from '@heroui/react';
import { Plus, Trash, ArrowUp, ArrowDown, FileText, MagnifyingGlass } from '@phosphor-icons/react';
import { useFileLabels } from 'hooks/useAssets';

// Extended type for file requirements with temporary IDs
export interface FileRequirementItem {
  id?: number;
  fileLabelId?: number;
  fileLabel?: {
    id: number;
    label: string;
  };
  requirementType: 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL';
  description?: string;
  active: boolean;
  displayOrder: number;
}

interface FileRequirementsBuilderProps {
  requirements: FileRequirementItem[];
  onChange: (requirements: FileRequirementItem[]) => void;
  className?: string;
}

export const FileRequirementsBuilder: React.FC<FileRequirementsBuilderProps> = ({
  requirements,
  onChange,
  className = '',
}) => {
  const { fileLabels, isLoading: isLoadingLabels } = useFileLabels();
  const [searchTerm, setSearchTerm] = useState('');

  const requirementTypeOptions = [
    { value: 'REQUIRED', label: 'Verplicht', color: 'danger' },
    { value: 'RECOMMENDED', label: 'Aanbevolen', color: 'warning' },
    { value: 'OPTIONAL', label: 'Optioneel', color: 'default' },
  ] as const;

  // Filter file labels based on search term
  const filteredFileLabels = useMemo(() => {
    if (!searchTerm.trim()) return fileLabels;

    const searchLower = searchTerm.toLowerCase();
    return fileLabels.filter((label) =>
      label.label.toLowerCase().includes(searchLower)
    );
  }, [fileLabels, searchTerm]);

  const handleAddRequirement = () => {
    const newRequirement: FileRequirementItem = {
      id: Date.now(), // Temporary ID for new requirements
      fileLabelId: undefined,
      requirementType: 'REQUIRED',
      description: '',
      active: true,
      displayOrder: requirements.length,
    };

    onChange([...requirements, newRequirement]);
  };

  const handleUpdateRequirement = (index: number, updates: Partial<FileRequirementItem>) => {
    const newRequirements = [...requirements];
    newRequirements[index] = { ...newRequirements[index], ...updates };
    onChange(newRequirements);
  };

  const handleRemoveRequirement = (index: number) => {
    const requirement = requirements[index];
    const labelName = requirement.fileLabel?.label || 'dit bestandsvereiste';

    if (!confirm(`Weet je zeker dat je "${labelName}" wilt verwijderen?`)) {
      return;
    }

    const newRequirements = requirements.filter((_, i) => i !== index);
    // Reorder remaining requirements
    newRequirements.forEach((req, i) => {
      req.displayOrder = i;
    });
    onChange(newRequirements);
  };

  const handleMoveRequirement = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === requirements.length - 1)
    ) {
      return;
    }

    const newRequirements = [...requirements];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap requirements
    [newRequirements[index], newRequirements[targetIndex]] = [
      newRequirements[targetIndex],
      newRequirements[index],
    ];

    // Update display order
    newRequirements.forEach((req, i) => {
      req.displayOrder = i;
    });

    onChange(newRequirements);
  };

  const handleFileLabelChange = (index: number, fileLabelId: number) => {
    const selectedLabel = fileLabels.find((label) => label.id === fileLabelId);
    handleUpdateRequirement(index, {
      fileLabelId,
      fileLabel: selectedLabel ? { id: selectedLabel.id, label: selectedLabel.label } : undefined,
    });
  };

  const getRequirementTypeColor = (type: string) => {
    const option = requirementTypeOptions.find((opt) => opt.value === type);
    return option?.color || 'default';
  };

  const renderRequirementEditor = (requirement: FileRequirementItem, index: number) => {
    const hasLabel = requirement.fileLabelId !== undefined;

    return (
      <Card key={requirement.id || index} className="mb-3">
        <CardBody className="p-4">
          <div className="flex items-center gap-3">
            {/* Move buttons */}
            <div className="flex flex-col gap-1">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => handleMoveRequirement(index, 'up')}
                isDisabled={index === 0}
              >
                <ArrowUp size={16} />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => handleMoveRequirement(index, 'down')}
                isDisabled={index === requirements.length - 1}
              >
                <ArrowDown size={16} />
              </Button>
            </div>

            {/* Main content */}
            <div className="flex-1 space-y-3">
              {/* File label and type selection */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex flex-col gap-2">
                    <Input
                      placeholder="Zoek bestandslabel..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="sm"
                      startContent={<MagnifyingGlass size={16} />}
                      isClearable
                      onClear={() => setSearchTerm('')}
                    />
                    <Select
                      label="Bestandslabel"
                      placeholder="Selecteer een bestandslabel"
                      selectedKeys={requirement.fileLabelId ? [requirement.fileLabelId.toString()] : []}
                      onSelectionChange={(keys) => {
                        const selectedKey = Array.from(keys)[0] as string;
                        if (selectedKey) {
                          handleFileLabelChange(index, parseInt(selectedKey));
                        }
                      }}
                      size="sm"
                      isDisabled={isLoadingLabels}
                      startContent={<FileText size={16} />}
                      isRequired
                    >
                      {filteredFileLabels.length === 0 && searchTerm ? (
                        <SelectItem key="no-results" isDisabled>
                          Geen bestandslabels gevonden
                        </SelectItem>
                      ) : (
                        filteredFileLabels.map((label) => (
                          <SelectItem key={label.id.toString()}>
                            {label.label}
                          </SelectItem>
                        ))
                      )}
                    </Select>
                  </div>
                </div>

                <div className="w-48">
                  <Select
                    label="Type"
                    selectedKeys={[requirement.requirementType]}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      handleUpdateRequirement(index, {
                        requirementType: selectedKey as 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL',
                      });
                    }}
                    size="sm"
                  >
                    {requirementTypeOptions.map((option) => (
                      <SelectItem key={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <Chip
                  size="sm"
                  color={getRequirementTypeColor(requirement.requirementType) as any}
                  variant="flat"
                >
                  {
                    requirementTypeOptions.find((opt) => opt.value === requirement.requirementType)
                      ?.label
                  }
                </Chip>
              </div>

              {/* Description (always visible if label is selected) */}
              {hasLabel && (
                <Textarea
                  label="Beschrijving (optioneel)"
                  placeholder="Wanneer is dit bestand nodig? Bijv. 'Bij aankoop van nieuw toestel' of 'Jaarlijks bij onderhoud'"
                  value={requirement.description || ''}
                  onChange={(e) => handleUpdateRequirement(index, { description: e.target.value })}
                  size="sm"
                  rows={2}
                />
              )}
            </div>

            {/* Delete button */}
            <Button
              isIconOnly
              size="sm"
              color="danger"
              variant="light"
              onPress={() => handleRemoveRequirement(index)}
            >
              <Trash size={18} />
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with explanation */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Bestandsvereisten</h3>
          <Button
            color="primary"
            size="sm"
            onPress={handleAddRequirement}
            startContent={<Plus size={16} />}
            isDisabled={isLoadingLabels}
          >
            Vereiste Toevoegen
          </Button>
        </div>

        {/* Help card */}
        <Card className="bg-blue-50 border border-blue-200">
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📄</div>
              <div className="flex-1 text-sm">
                <p className="font-semibold text-blue-900 mb-2">
                  Stap 4: Bepaal welke documenten vereist zijn
                </p>
                <p className="text-blue-800 mb-2">
                  Koppel bestandslabels die altijd bij deze voorziening horen.
                </p>
                <p className="text-blue-700 text-xs">
                  <strong>Voorbeelden voor "Elektrische toestellen":</strong>
                  <br />• Garantiebewijs (verplicht)
                  <br />• Installatiehandleiding (aanbevolen)
                  <br />• Serienummer foto (verplicht)
                  <br />• Technische fiche (optioneel)
                </p>
                <p className="text-blue-600 text-xs mt-2 italic">
                  Wanneer je later een voorziening toevoegt, herinnert het systeem je om deze
                  documenten te uploaden.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Loading state */}
      {isLoadingLabels && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" />
          <span className="ml-3 text-gray-600">Bestandslabels laden...</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoadingLabels && requirements.length === 0 && (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <FileText size={48} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500 text-sm font-medium mb-1">
            Nog geen bestandsvereisten gedefinieerd
          </p>
          <p className="text-gray-400 text-xs mb-4">
            Klik "Vereiste Toevoegen" om documenten te koppelen aan deze categorie
          </p>
          <div className="max-w-md mx-auto text-left bg-white p-3 rounded-md border border-gray-200 text-xs text-gray-600">
            <p className="font-semibold mb-1">💡 Tip:</p>
            <p>
              Bestandsvereisten helpen je om consistentie te bewaren. Bijvoorbeeld: als je weet dat
              elke elektrische toestel een garantiebewijs moet hebben, voeg je dit hier toe. Later
              zal het systeem je herinneren om dit document te uploaden.
            </p>
          </div>
        </div>
      )}

      {/* Requirements list */}
      {!isLoadingLabels && requirements.length > 0 && (
        <div className="space-y-0">
          {requirements
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((requirement, index) => renderRequirementEditor(requirement, index))}
        </div>
      )}

      {/* Summary */}
      {!isLoadingLabels && requirements.length > 0 && (
        <Card className="bg-gray-50 border border-gray-200">
          <CardBody className="p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">
                <strong>{requirements.length}</strong> bestandsvereiste(n) geconfigureerd
              </span>
              <div className="flex items-center gap-2">
                {requirements.filter((r) => r.requirementType === 'REQUIRED').length > 0 && (
                  <Chip size="sm" color="danger" variant="flat">
                    {requirements.filter((r) => r.requirementType === 'REQUIRED').length} verplicht
                  </Chip>
                )}
                {requirements.filter((r) => r.requirementType === 'RECOMMENDED').length > 0 && (
                  <Chip size="sm" color="warning" variant="flat">
                    {requirements.filter((r) => r.requirementType === 'RECOMMENDED').length}{' '}
                    aanbevolen
                  </Chip>
                )}
                {requirements.filter((r) => r.requirementType === 'OPTIONAL').length > 0 && (
                  <Chip size="sm" color="default" variant="flat">
                    {requirements.filter((r) => r.requirementType === 'OPTIONAL').length} optioneel
                  </Chip>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
